// src/utils/ttl-cache.ts
// Simple TTL (Time To Live) Cache Implementation
// Replaces complex Universal Cache with simple in-memory caching
// RELEVANT FILES: src/api/cached-api.ts, src/api/ringcentral-api.ts

import { getLocalDateString } from './date';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Simple TTL Cache Class
 * Stores data with expiration times in memory
 */
class TTLCache<T = any> {
  private cache = new Map<string, CacheEntry<T>>();
  private defaultTTL: number;

  constructor(defaultTTL: number = 5 * 60 * 1000) {
    // 5 minutes default
    this.defaultTTL = defaultTTL;
  }

  /**
   * Set data in cache with TTL
   */
  set(key: string, data: T, ttl?: number): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
    };
    this.cache.set(key, entry);
  }

  /**
   * Get data from cache if not expired
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {return null;}

    const now = Date.now();
    const isExpired = now - entry.timestamp > entry.ttl;

    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Get data or fetch with fallback function
   */
  async getOrFetch<R = T>(
    key: string,
    fetchFn: () => Promise<R>,
    options?: {
      ttl?: number;
      force?: boolean;
    },
  ): Promise<R> {
    // Force refresh bypasses cache
    if (options?.force) {
      const data = await fetchFn();
      this.set(key, data as unknown as T, options.ttl);
      return data;
    }

    // Try to get from cache first
    const cached = this.get(key);
    if (cached !== null) {
      return cached as unknown as R;
    }

    // Fetch and cache
    const data = await fetchFn();
    this.set(key, data as unknown as T, options?.ttl);
    return data;
  }

  /**
   * Check if key exists in cache and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Delete specific key
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   */
  stats(): {
    size: number;
    entries: Array<{
      key: string;
      timestamp: number;
      ttl: number;
      remainingMs: number;
      expired: boolean;
    }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      timestamp: entry.timestamp,
      ttl: entry.ttl,
      remainingMs: Math.max(0, entry.ttl - (now - entry.timestamp)),
      expired: now - entry.timestamp > entry.ttl,
    }));

    return {
      size: this.cache.size,
      entries,
    };
  }
}

/**
 * Global cache instances with different TTL for different data types
 */

// Daily analytics cache - short TTL for current day data
export const dailyAnalyticsCache = new TTLCache(5 * 60 * 1000); // 5 minutes

// Call details cache - medium TTL
export const callDetailsCache = new TTLCache(7 * 24 * 60 * 60 * 1000); // 7 days

// User memberships cache - medium TTL
export const userCache = new TTLCache(60 * 60 * 1000); // 1 hour

// RingCentral connections cache - medium TTL
export const ringCentralCache = new TTLCache(24 * 60 * 60 * 1000); // 24 hours

// Audio files cache - long TTL (stored as blob URLs)
export const audioCache = new TTLCache(30 * 24 * 60 * 60 * 1000); // 30 days

/**
 * Smart TTL based on date - shorter TTL for current day data
 */
export function getSmartTTL(date?: string): number {
  const today = getLocalDateString();
  const targetDate = date || today;

  if (targetDate === today) {
    return 5 * 60 * 1000; // 5 minutes for current day
  }
    return 7 * 24 * 60 * 60 * 1000; // 7 days for historical data
  
}

/**
 * Cleanup all caches periodically
 */
export function startCacheCleanup(): void {
  // Run cleanup every 10 minutes
  setInterval(
    () => {
      dailyAnalyticsCache.cleanup();
      callDetailsCache.cleanup();
      userCache.cleanup();
      ringCentralCache.cleanup();
      audioCache.cleanup();
      console.log('🧹 Cache cleanup completed');
    },
    10 * 60 * 1000,
  );
}

/**
 * Clear all caches
 */
export function clearAllCaches(): void {
  dailyAnalyticsCache.clear();
  callDetailsCache.clear();
  userCache.clear();
  ringCentralCache.clear();
  audioCache.clear();
  console.log('🗑️ All caches cleared');
}

/**
 * Get cache statistics for all caches
 */
export function getAllCacheStats() {
  return {
    dailyAnalytics: dailyAnalyticsCache.stats(),
    callDetails: callDetailsCache.stats(),
    user: userCache.stats(),
    ringCentral: ringCentralCache.stats(),
    audio: audioCache.stats(),
  };
}

// Start cleanup when module loads
startCacheCleanup();
